import { Directive, inject } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { AbstractAdyenPaymentComponent } from '../abstract-adyen-payment.component';
import { Adyen } from '../adyen.namespace';

@Directive()
export abstract class AdyenApplePayComponentDef extends AbstractAdyenPaymentComponent<
    Adyen.IApplePayPaymentMethod,
    Adyen.IApplePayComponentOptions
> {
    protected readonly methodCode = Adyen.PaymentMethod.ApplePay;

    /**
     * Selector for component container
     * @type {string}
     */
    protected readonly containerSelector = '#adyen-apple-pay-container';

    /**
     * Adyen checkout component id
     * @type  {string}
     */
    protected readonly componentId = Adyen.Component.ApplePay;

    /**
     * Adyen checkout component instance
     */
    protected component?: Adyen.IApplePayComponent;

    applePayAvailableStatus = new Status();

    validateMerchantStatus = new Status();

    /**
     * Flag if component was already initiated
     * @type {boolean}
     */
    protected componentInitiated = false;

    private lastCountryCode = '';

    private lastTotal?: number;

    private timeoutService = inject(TimeoutService);

    private onFormsChangeDebounced = this.timeoutService.debounce(() => this.onFormsChange(), 200);

    private initComponentDebounced = this.timeoutService.debounce(() => this.initComponent(), 200);

    /**
     * Initiates adyen component
     */
    protected initComponent(): void {
        if (!this.componentInitiated) {
            this.componentInitiated = true;
            this.subscriptions.add(
                this.app.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => {
                    if (this.getTotal() !== this.lastTotal) {
                        this.initComponentDebounced();
                    }
                })
            );
            this.checkoutStepComponent.forms.forEach(form =>
                this.subscriptions.add(form.valueChanges.subscribe(this.onFormsChangeDebounced))
            );
            this.subscriptions.add(this.validateMerchantStatus.subscribe(() => this.detectChanges()));
            this.subscriptions.add(this.applePayAvailableStatus.subscribe(() => this.detectChanges()));
        }
        this.unmount();
        const component = this.getComponent() as Adyen.IApplePayComponent | undefined;
        if (component) {
            this.mount(component);
        } else if (!this.getBillingCountryCode()) {
            this.applePayAvailableStatus.error = 'Please select billing address country.';
        }
    }

    protected onFormsChange(): void {
        if (this.getBillingCountryCode() !== this.lastCountryCode) {
            this.initComponentDebounced();
        }
        this.detectChanges();
    }

    /**
     * Returns adyen apple pay component configuration
     * @returns {Adyen.IApplePayComponentOptions}
     */
    protected getComponentOptions(): Adyen.IApplePayComponentOptions | undefined {
        const countryCode = this.getBillingCountryCode();
        const amount = this.getTotal();
        this.lastCountryCode = countryCode;
        this.lastTotal = amount;
        if (countryCode && amount) {
            const options: Adyen.IApplePayComponentOptions = {
                currencyCode: this.app.data.currency.base,
                amount,
                countryCode,
                configuration: {
                    merchantName: this.app.data.name,
                    merchantIdentifier: this.method.merchantIdentifier
                },
                onValidateMerchant: (resolve, reject, validationURL) => {
                    this.app.request.send(
                        this.validateMerchantStatus,
                        { path: Adyen.Endpoints.ApplePaySession },
                        response => resolve(response),
                        () => reject()
                    );
                },
                onAuthorized: (resolve, reject, event: ApplePay.IPaymentEvent) => {
                    if (this.isCheckoutValid) {
                        const submissionData = this.checkoutFormComponent.getSubmissionData();
                        submissionData.paymentMethod = {
                            method: this.methodCode,
                            additional_data: {
                                token: JSON.stringify(event.payment)
                            }
                        };
                        this.checkoutFormComponent.submitWithData(
                            submissionData,
                            () => resolve(),
                            () => reject()
                        );
                    } else {
                        reject();
                        this.validateCheckout();
                    }
                }
            };
            return options;
        }
        return undefined;
    }

    /**
     * Returns billing country code required for createing apple pay component
     * @returns {string | undefined}
     */
    protected getBillingCountryCode(): string | undefined {
        const billingAddress = this.checkoutFormComponent.billingAddress;
        return billingAddress ? billingAddress.country_id : undefined;
    }

    /**
     * Mounts adyen component in container
     * @param {Adyen.IApplePayComponent} adyenComponent
     */
    protected mount(adyenComponent: Adyen.IApplePayComponent): void {
        adyenComponent
            .isAvailable()
            .then(() => {
                // we have to 'unmount' again because of 'isAvailable' promise, other instance could be mounted in meantime
                this.unmount();
                this.component = adyenComponent;
                this.applePayAvailableStatus.success = 'SUCCESS';
                super.mount(adyenComponent);
            })
            .catch(() => {
                this.applePayAvailableStatus.error = 'Apple Pay is not available. Please select another payment method.';
            });
    }

    /**
     * Override, this method use specific submit button/flow
     */
    protected onCheckoutSubmit(): void {}

    /**
     * Check if all steps in checkout are valid.
     * Used to show additional last step or content if entire checkout is valid.
     * (e.g on OB show 'Review your order' section)
     */
    get isCheckoutValid(): boolean {
        return this.checkoutFormComponent.isCheckoutValid;
    }

    /**
     * Validate checkout (e.g on submit) and scroll to first error if any.
     */
    validateCheckout(): void {
        this.checkoutFormComponent.validateCheckout();
    }

    /**
     * Converts amount to match adyen converter
     * @param {number} value
     * @returns {number}
     */
    protected getAmount(value: number): number {
        const converter = Adyen.currencyConverterMap[this.app.data.currency.base] || 100;
        return parseInt((value * converter).toFixed(), 10);
    }

    /**
     * Returns total amount to pay
     * @returns {number | undefined}
     */
    protected getTotal(): number | undefined {
        const total = this.app.customer.basket.data?.totals?.grand_total;
        if (total !== undefined) {
            return this.getAmount(total);
        }
        return total;
    }
}